import { newId } from './ids';
import type { ModuleId } from './modules';

export type MutationOp = 'insert' | 'update' | 'delete';
export type MutationSource = ModuleId | 'system';

export interface Mutation {
  /** newId(); the idempotency key for sync. */
  id: string;
  /** A TableName from @tahi/db (kept as string in core to avoid the dependency). */
  table: string;
  op: MutationOp;
  rowId: string;
  /**
   * Keyed by Drizzle property names (camelCase, e.g. amountMinor).
   * insert: every column except the reserved ones; update: changed fields only; delete: absent.
   */
  values?: Record<string, unknown>;
  /** ISO UTC, device clock. */
  at: string;
  source: MutationSource;
  /** Id of the mutation that triggered this one via a rule (the connection chain). */
  causedBy?: string;
  /**
   * Insert only. If rowId already exists (live or soft-deleted), the applier acknowledges the
   * mutation and changes nothing. Used for seeded defaults so they never overwrite user edits.
   * A row it does create gets floor clock entries (seedClockKey), so any real edit beats it.
   */
  ifAbsent?: true;
}

/**
 * Columns the applier fills from the mutation itself (rowId, the signed-in user, `at`),
 * so they never appear in `values`.
 */
export const RESERVED_KEYS = [
  'id',
  'userId',
  'createdAt',
  'updatedAt',
  'deletedAt',
  'clock',
] as const;

interface BaseArgs {
  table: string;
  source: MutationSource;
  /** Defaults to now. */
  at?: string;
  causedBy?: string;
}

function checkValues(values: Record<string, unknown>): void {
  for (const key of RESERVED_KEYS) {
    if (key in values) throw new Error(`Mutation values must not include reserved key '${key}'`);
  }
}

function base(args: BaseArgs, op: MutationOp, rowId: string): Mutation {
  // Always full ISO (with millis) so last-write-wins can compare timestamps as text.
  const date = new Date(args.at ?? Date.now());
  if (Number.isNaN(date.getTime()))
    throw new RangeError(`Invalid mutation 'at': ${String(args.at)}`);
  const m: Mutation = {
    id: newId(),
    table: args.table,
    op,
    rowId,
    at: date.toISOString(),
    source: args.source,
  };
  if (args.causedBy !== undefined) m.causedBy = args.causedBy;
  return m;
}

export function insertMutation(
  args: BaseArgs & { rowId?: string; values: Record<string, unknown>; ifAbsent?: true },
): Mutation {
  checkValues(args.values);
  const m: Mutation = {
    ...base(args, 'insert', args.rowId ?? newId()),
    values: { ...args.values },
  };
  if (args.ifAbsent) m.ifAbsent = true;
  return m;
}

export function updateMutation(
  args: BaseArgs & { rowId: string; values: Record<string, unknown> },
): Mutation {
  checkValues(args.values);
  if (Object.keys(args.values).length === 0) {
    throw new Error('An update mutation needs at least one changed field');
  }
  return { ...base(args, 'update', args.rowId), values: { ...args.values } };
}

/** A soft delete: the applier sets deletedAt = at. */
export function deleteMutation(args: BaseArgs & { rowId: string }): Mutation {
  return base(args, 'delete', args.rowId);
}
